import React from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faJsSquare, faPhp, faPython } from '@fortawesome/free-brands-svg-icons';
import type { Translate } from '../../../components/apidocs';

/* ── ตาราง Webhook Events ──────────────────────────────────── */

type WebhookEvent = {
  event: string;
  /** สีของชื่อ event */
  codeColor: string;
  kind: 'Payin' | 'Payout' | 'Payout (P2P)';
  descriptionKey: string;
  descriptionFallback?: string;
};

const EVENTS: WebhookEvent[] = [
  { event: 'order.paid', codeColor: 'text-emerald-600', kind: 'Payin', descriptionKey: 'apiDocs.eventOrderPaid' },
  { event: 'order.cancelled', codeColor: 'text-rose-600', kind: 'Payin', descriptionKey: 'apiDocs.eventOrderCancelled', descriptionFallback: 'Order ถูกยกเลิกโดยแอดมิน (เฉพาะออเดอร์ที่ยังไม่ชำระเงิน)' },
  { event: 'payout.approved', codeColor: 'text-sky-600', kind: 'Payout', descriptionKey: 'apiDocs.eventPayoutApproved' },
  { event: 'payout.rejected', codeColor: 'text-rose-600', kind: 'Payout', descriptionKey: 'apiDocs.eventPayoutRejected' },
  { event: 'payout.funded', codeColor: 'text-indigo-600', kind: 'Payout (P2P)', descriptionKey: 'apiDocs.eventPayoutFunded', descriptionFallback: 'Payout ได้รับเงินเข้าบางส่วน (ยิงทุกงวดที่เงินเข้า, status ยังเป็น pending) — เฉพาะร้านที่เปิดโหมดจ่ายแบบ P2P' },
  { event: 'payout.completed', codeColor: 'text-violet-600', kind: 'Payout', descriptionKey: 'apiDocs.eventPayoutCompleted' },
];

/** badge ประเภท event — Payin ใช้โทนเขียว, Payout ใช้โทนฟ้า (ตามของเดิม), P2P ใช้โทนคราม */
const KIND_BADGE: Record<WebhookEvent['kind'], string> = {
  Payin: 'bg-emerald-100 text-emerald-700',
  Payout: 'bg-sky-100 text-sky-700',
  'Payout (P2P)': 'bg-indigo-100 text-indigo-700',
};

export const WEBHOOK_EVENT_HEADERS = ['Event', 'Type', 'Description'];

export const buildWebhookEventRows = (t: Translate): React.ReactNode[][] => EVENTS.map(ev => [
  <code className={`${ev.codeColor} font-mono text-xs font-medium`}>{ev.event}</code>,
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${KIND_BADGE[ev.kind]}`}>{ev.kind}</span>,
  ev.descriptionFallback ? t(ev.descriptionKey, ev.descriptionFallback) : t(ev.descriptionKey),
]);

/* ── ตัวอย่าง payload ของแต่ละ event (แท็บ Webhooks) ──────── */

export const WEBHOOK_PAYLOAD_SAMPLES: Array<{ event: string; color: string; label: string; payload: string }> = [
  {
    event: 'order.paid', color: 'text-emerald-500', label: 'Payload: order.paid (Payin)', payload: `{
  "event": "order.paid",
  "order_number": "ORD_abc123xyz",
  "status": "paid",
  "base_amount": 100.00,
  "total_amount": 100.45,
  "slip_amount": 100.45,
  "requested_amount": null,
  "amount_mismatch": false,
  "customer_name": "นาย A",
  "customer_email": "customer@example.com",
  "customer_phone": "0812345678",
  "reference_id": "YOUR-ORDER-123",
  "paid_at": "2025-10-09T12:35:29.000Z",
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:35:30.000Z"
}`,
  },
  {
    event: 'order.cancelled', color: 'text-rose-500', label: 'Payload: order.cancelled (Payin)', payload: `{
  "event": "order.cancelled",
  "order_number": "ORD_abc123xyz",
  "status": "cancelled",
  "base_amount": 100.00,
  "total_amount": 100.45,
  "slip_amount": null,
  "requested_amount": null,
  "amount_mismatch": false,
  "customer_name": "นาย A",
  "customer_email": "customer@example.com",
  "customer_phone": "0812345678",
  "reference_id": "YOUR-ORDER-123",
  "paid_at": null,
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:36:00.000Z"
}`,
  },
  {
    event: 'payout.approved', color: 'text-sky-500', label: 'Payload: payout.approved', payload: `{
  "event": "payout.approved",
  "payout_number": "PAYOUT20251009001",
  "status": "approved",
  "amount": 1000.00,
  "fee_amount": 0,
  "net_amount": 1000.00,
  "bank_account_name": "นาย A",
  "bank_account_number": "1234567890",
  "bank_name": "SCB",
  "slip_url": null,
  "rejected_reason": null,
  "approved_at": "2025-10-09T12:35:00.000Z",
  "reference_id": "YOUR-ORDER-123",
  "completed_at": null,
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:35:01.000Z"
}`,
  },
  {
    event: 'payout.rejected', color: 'text-rose-500', label: 'Payload: payout.rejected', payload: `{
  "event": "payout.rejected",
  "payout_number": "PAYOUT20251009001",
  "status": "rejected",
  "amount": 1000.00,
  "fee_amount": 0,
  "net_amount": 1000.00,
  "bank_account_name": "นาย A",
  "bank_account_number": "1234567890",
  "bank_name": "SCB",
  "slip_url": null,
  "rejected_reason": "เลขบัญชีไม่ถูกต้อง",
  "reference_id": "YOUR-ORDER-123",
  "approved_at": null,
  "completed_at": null,
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:35:01.000Z"
}`,
  },
  {
    event: 'payout.completed', color: 'text-violet-500', label: 'Payload: payout.completed', payload: `{
  "event": "payout.completed",
  "payout_number": "PAYOUT20251009001",
  "status": "completed",
  "amount": 1000.00,
  "fee_amount": 0,
  "net_amount": 1000.00,
  "bank_account_name": "นาย A",
  "bank_account_number": "1234567890",
  "bank_name": "SCB",
  "slip_url": "https://storage.example.com/slips/payout123.jpg",
  "rejected_reason": null,
  "reference_id": "YOUR-ORDER-123",
  "approved_at": "2025-10-09T12:35:00.000Z",
  "completed_at": "2025-10-09T12:40:00.000Z",
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:40:01.000Z"
}`,
  },
  // ⭐ P2P: ยิงทุกงวดที่เงินเข้า — field order ตรงกับ webhookService.sendPayoutWebhook จริง
  //   (reference_id อยู่หลัง timestamp, 3 ฟิลด์ funding ต่อท้าย เฉพาะเมื่อเป็น P2P)
  {
    event: 'payout.funded', color: 'text-indigo-500', label: 'Payload: payout.funded (P2P — เงินเข้าบางส่วน, ยิงทุกงวด)', payload: `{
  "event": "payout.funded",
  "payout_number": "PAYOUT20251009001",
  "status": "pending",
  "amount": 10000.00,
  "fee_amount": 50.00,
  "net_amount": 10000.00,
  "bank_account_name": "นาย A",
  "bank_account_number": "1234567890",
  "bank_name": "SCB",
  "slip_url": null,
  "rejected_reason": null,
  "approved_at": null,
  "completed_at": null,
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T12:35:30.000Z",
  "reference_id": "YOUR-REF-123",
  "paid_amount": 3000.00,
  "funded_amount": 3000.00,
  "remaining_amount": 7000.00
}`,
  },
  {
    event: 'payout.completed-p2p', color: 'text-indigo-500', label: 'Payload: payout.completed (P2P — ยอดครบแล้ว, มีฟิลด์ funding เพิ่ม)', payload: `{
  "event": "payout.completed",
  "payout_number": "PAYOUT20251009001",
  "status": "completed",
  "amount": 10000.00,
  "fee_amount": 50.00,
  "net_amount": 10000.00,
  "bank_account_name": "นาย A",
  "bank_account_number": "1234567890",
  "bank_name": "SCB",
  "slip_url": null,
  "rejected_reason": null,
  "approved_at": null,
  "completed_at": "2025-10-09T13:05:00.000Z",
  "created_at": "2025-10-09T12:30:29.000Z",
  "timestamp": "2025-10-09T13:05:01.000Z",
  "reference_id": "YOUR-REF-123",
  "paid_amount": 4000.00,
  "funded_amount": 10000.00,
  "remaining_amount": 0
}`,
  },
];

/** ตัวอย่างโค้ด verify signature (แท็บ Webhooks) */
export const WEBHOOK_VERIFY_SAMPLE = `const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const hmac = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');

  return hmac === signature;
}

// Express.js example
app.post('/webhook', (req, res) => {
  const signature = req.headers['x-webhook-signature'];
  const isValid = verifyWebhook(req.body, signature, 'YOUR_WEBHOOK_SECRET');

  if (!isValid) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  // Process webhook
  const { event, order_number, payout_number } = req.body;
  console.log('Received event:', event);

  res.json({ received: true });
});`;

/* ── Webhook Toolbox: Payload Builder ──────────────────────── */

export const WEBHOOK_BUILDER_EVENTS = [
  { id: 'order.paid', label: 'order.paid', color: 'emerald' },
  { id: 'order.cancelled', label: 'order.cancelled', color: 'rose' },
  { id: 'payout.approved', label: 'payout.approved', color: 'sky' },
  { id: 'payout.rejected', label: 'payout.rejected', color: 'rose' },
  { id: 'payout.funded', label: 'payout.funded (P2P)', color: 'violet' },
  { id: 'payout.completed', label: 'payout.completed', color: 'violet' },
];

/** payload ตัวอย่างสำหรับยิงทดสอบ — สร้างใหม่ทุกครั้งเพื่อให้ timestamp/เลขที่รายการสดเสมอ */
export const buildBuilderPayloads = (): Record<string, Record<string, unknown>> => {
  const now = new Date().toISOString();
  return {
    'order.paid': {
      event: 'order.paid',
      order_number: 'ORD_test_' + Date.now(),
      status: 'paid',
      base_amount: 100.00,
      total_amount: 100.45,
      slip_amount: 100.45,
      requested_amount: null,
      amount_mismatch: false,
      customer_name: 'ทดสอบ ระบบ',
      customer_email: 'test@example.com',
      customer_phone: '0812345678',
      reference_id: 'REF-TEST-001',
      paid_at: now,
      created_at: now,
      timestamp: now,
    },
    'order.cancelled': {
      event: 'order.cancelled',
      order_number: 'ORD_test_' + Date.now(),
      status: 'cancelled',
      base_amount: 100.00,
      total_amount: 100.45,
      slip_amount: null,
      requested_amount: null,
      amount_mismatch: false,
      customer_name: 'ทดสอบ ระบบ',
      customer_email: 'test@example.com',
      customer_phone: '0812345678',
      reference_id: 'REF-TEST-001',
      paid_at: null,
      created_at: now,
      timestamp: now,
    },
    'payout.approved': {
      event: 'payout.approved',
      payout_number: 'PAYOUT_TEST_' + Date.now(),
      status: 'approved',
      amount: 1000.00,
      fee_amount: 10.00,
      net_amount: 990.00,
      bank_account_name: 'ทดสอบ ระบบ',
      bank_account_number: '1234567890',
      bank_name: 'SCB',
      slip_url: null,
      rejected_reason: null,
      reference_id: 'PAYOUT-REF-001',
      approved_at: now,
      completed_at: null,
      created_at: now,
      timestamp: now,
    },
    'payout.rejected': {
      event: 'payout.rejected',
      payout_number: 'PAYOUT_TEST_' + Date.now(),
      status: 'rejected',
      amount: 1000.00,
      fee_amount: 0,
      net_amount: 1000.00,
      bank_account_name: 'ทดสอบ ระบบ',
      bank_account_number: '9999999999',
      bank_name: 'SCB',
      slip_url: null,
      rejected_reason: 'เลขบัญชีไม่ถูกต้อง',
      reference_id: 'PAYOUT-REF-002',
      approved_at: null,
      completed_at: null,
      created_at: now,
      timestamp: now,
    },
    // ⭐ P2P: เงินเข้าบางส่วน (ยิงทุกงวด) — มี paid/funded/remaining ต่อท้าย
    'payout.funded': {
      event: 'payout.funded',
      payout_number: 'PAYOUT_TEST_' + Date.now(),
      status: 'pending',
      amount: 10000.00,
      fee_amount: 50.00,
      net_amount: 10000.00,
      bank_account_name: 'ทดสอบ ระบบ',
      bank_account_number: '1234567890',
      bank_name: 'SCB',
      slip_url: null,
      rejected_reason: null,
      approved_at: null,
      completed_at: null,
      created_at: now,
      timestamp: now,
      reference_id: 'PAYOUT-REF-004',
      paid_amount: 3000.00,
      funded_amount: 3000.00,
      remaining_amount: 7000.00,
    },
    'payout.completed': {
      event: 'payout.completed',
      payout_number: 'PAYOUT_TEST_' + Date.now(),
      status: 'completed',
      amount: 1000.00,
      fee_amount: 10.00,
      net_amount: 990.00,
      bank_account_name: 'ทดสอบ ระบบ',
      bank_account_number: '1234567890',
      bank_name: 'SCB',
      slip_url: 'https://storage.example.com/slips/test123.jpg',
      rejected_reason: null,
      reference_id: 'PAYOUT-REF-003',
      approved_at: now,
      completed_at: now,
      created_at: now,
      timestamp: now,
    },
  };
};

/* ── Webhook Toolbox: Code Generator ───────────────────────── */

export const WEBHOOK_CODEGEN_LANGUAGES: Array<{ id: string; icon: IconDefinition; label: string; codeId: string; fileName: string }> = [
  { id: 'javascript', icon: faJsSquare, label: 'Node.js', codeId: 'wh-code-js', fileName: 'webhook-handler.js' },
  { id: 'php', icon: faPhp, label: 'PHP', codeId: 'wh-code-php', fileName: 'webhook.php' },
  { id: 'python', icon: faPython, label: 'Python', codeId: 'wh-code-py', fileName: 'webhook_handler.py' },
];

/** โค้ด webhook handler พร้อมใช้ ของแต่ละภาษา */
export const WEBHOOK_HANDLER_SAMPLES: Record<string, string> = {
  javascript: `const express = require('express');
const crypto = require('crypto');
const app = express();

// ⚠️ ใช้ raw body สำหรับ verify signature
app.use('/webhook', express.raw({ type: 'application/json' }));

const WEBHOOK_SECRET = process.env.WEBHOOK_SECRET || 'your_webhook_secret';

function verifySignature(rawBody, signature) {
  const expected = crypto
    .createHmac('sha256', WEBHOOK_SECRET)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(signature, 'hex')
  );
}

app.post('/webhook', (req, res) => {
  const signature = req.headers['x-webhook-signature'];

  // 1. ตรวจ signature
  if (!signature || !verifySignature(req.body, signature)) {
    console.error('❌ Invalid signature');
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const payload = JSON.parse(req.body.toString());
  console.log('✅ Webhook received:', payload.event);

  // 2. แยกประเภท event
  switch (payload.event) {
    case 'order.paid':
      console.log('💰 Order paid:', payload.order_number);
      console.log('   Amount:', payload.total_amount, 'THB');
      // TODO: อัปเดต order status ในระบบของคุณ
      break;

    case 'payout.approved':
      console.log('✅ Payout approved:', payload.payout_number);
      break;

    case 'payout.completed':
      console.log('🎉 Payout completed:', payload.payout_number);
      console.log('   Slip:', payload.slip_url);
      // TODO: บันทึก slip URL
      break;

    case 'payout.rejected':
      console.log('❌ Payout rejected:', payload.payout_number);
      console.log('   Reason:', payload.rejected_reason);
      // TODO: แจ้งเตือนทีม
      break;

    default:
      console.log('Unknown event:', payload.event);
  }

  // 3. ตอบ 200 ทันที (ห้ามรอ process นาน)
  res.json({ received: true });
});

app.listen(3000, () => console.log('Webhook server on :3000'));`,

  php: `<?php
// webhook.php

$webhookSecret = getenv('WEBHOOK_SECRET') ?: 'your_webhook_secret';

// 1. อ่าน raw body (ห้ามใช้ $_POST)
$rawBody = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '';

// 2. ตรวจ signature
$expected = hash_hmac('sha256', $rawBody, $webhookSecret);
if (!hash_equals($expected, $signature)) {
    http_response_code(401);
    echo json_encode(['error' => 'Invalid signature']);
    exit;
}

// 3. Parse payload
$payload = json_decode($rawBody, true);
$event = $payload['event'] ?? '';

// 4. แยกประเภท event
switch ($event) {
    case 'order.paid':
        $orderNumber = $payload['order_number'];
        $amount = $payload['total_amount'];
        // TODO: อัปเดต order status ในระบบ
        error_log("Order paid: $orderNumber - $amount THB");
        break;

    case 'payout.approved':
        $payoutNumber = $payload['payout_number'];
        error_log("Payout approved: $payoutNumber");
        break;

    case 'payout.completed':
        $payoutNumber = $payload['payout_number'];
        $slipUrl = $payload['slip_url'];
        // TODO: บันทึก slip URL
        error_log("Payout completed: $payoutNumber - Slip: $slipUrl");
        break;

    case 'payout.rejected':
        $payoutNumber = $payload['payout_number'];
        $reason = $payload['rejected_reason'];
        // TODO: แจ้งเตือนทีม
        error_log("Payout rejected: $payoutNumber - $reason");
        break;

    default:
        error_log("Unknown webhook event: $event");
}

// 5. ตอบ 200 ทันที
http_response_code(200);
echo json_encode(['received' => true]);`,

  python: `import hmac
import hashlib
import json
import os
from flask import Flask, request, jsonify

app = Flask(__name__)

WEBHOOK_SECRET = os.environ.get('WEBHOOK_SECRET', 'your_webhook_secret')

def verify_signature(raw_body: bytes, signature: str) -> bool:
    expected = hmac.new(
        WEBHOOK_SECRET.encode(),
        raw_body,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)

@app.route('/webhook', methods=['POST'])
def webhook():
    # 1. ตรวจ signature
    signature = request.headers.get('X-Webhook-Signature', '')
    raw_body = request.get_data()

    if not verify_signature(raw_body, signature):
        print('❌ Invalid signature')
        return jsonify({'error': 'Invalid signature'}), 401

    payload = request.get_json()
    event = payload.get('event', '')
    print(f'✅ Webhook received: {event}')

    # 2. แยกประเภท event
    if event == 'order.paid':
        order_number = payload['order_number']
        amount = payload['total_amount']
        print(f'💰 Order paid: {order_number} - {amount} THB')
        # TODO: อัปเดต order status ในระบบ

    elif event == 'payout.approved':
        payout_number = payload['payout_number']
        print(f'✅ Payout approved: {payout_number}')

    elif event == 'payout.completed':
        payout_number = payload['payout_number']
        slip_url = payload.get('slip_url')
        print(f'🎉 Payout completed: {payout_number} - Slip: {slip_url}')
        # TODO: บันทึก slip URL

    elif event == 'payout.rejected':
        payout_number = payload['payout_number']
        reason = payload.get('rejected_reason')
        print(f'❌ Payout rejected: {payout_number} - {reason}')
        # TODO: แจ้งเตือนทีม

    else:
        print(f'Unknown event: {event}')

    # 3. ตอบ 200 ทันที
    return jsonify({'received': True})

if __name__ == '__main__':
    app.run(port=3000, debug=True)`,
};
